import { Container } from "react-bootstrap";
import HeaderLanding from "../components/HeaderLanding";
import Tail from "../components/Tail";

export default function Privacy() {
  return (
    <div className="d-flex flex-column min-vh-100">
      <HeaderLanding />

      <main className="flex-grow-1">
        <Container className="py-5">
          <div className="privacy-content">
            <h1 className="mb-4">Privacy Policy</h1>
            <p className="text-muted">Last Updated: {new Date().toLocaleDateString()}</p>

            <section className="mb-5">
              <h2 className="h4 mb-3">1. Introduction</h2>
              <p>
                Pic-Loud ("Company," "we," "us," or "our") operates the Pic-Loud platform (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our Service.
              </p>
              <p>
                Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our Service.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">2. Information We Collect</h2>

              <h5 className="mt-4 mb-3">2.1 Information You Provide Directly</h5>
              <ul className="ms-3">
                <li><strong>Account Registration:</strong> Name, email address, phone number, password</li>
                <li><strong>Payment Information:</strong> Credit card details, billing address, transaction history (processed securely by third-party payment processors)</li>
                <li><strong>Biometric Data:</strong> Face matrix (facial encoding) created from selfies and uploaded photos</li>
                <li><strong>Profile Information:</strong> Profile pictures, event participation details, preferences</li>
                <li><strong>Communication:</strong> Messages, support requests, feedback</li>
              </ul>

              <h5 className="mt-4 mb-3">2.2 Automatically Collected Information</h5>
              <ul className="ms-3">
                <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
                <li><strong>Usage Analytics:</strong> Pages visited, time spent, features used, clicks, interactions</li>
                <li><strong>Cookies &amp; Tracking:</strong> Session cookies, persistent cookies for preferences and authentication</li>
                <li><strong>Location Data:</strong> Approximate location based on IP address (not GPS tracking)</li>
              </ul>

              <h5 className="mt-4 mb-3">2.3 Information from Third Parties</h5>
              <ul className="ms-3">
                <li>Payment processors for transaction verification</li>
                <li>Social media platforms if you connect your accounts</li>
                <li>Event organizers who upload photos containing your image</li>
              </ul>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">3. Biometric Data Processing</h2>
              <p>
                <strong>What is Biometric Data?</strong> When you upload a selfie or we process photos of you, we create a "face matrix" — a numerical representation of your facial features. This is not a photo; it's a mathematical encoding that cannot be reversed to recreate your image.
              </p>

              <h5 className="mt-4 mb-3">3.1 How We Use Biometric Data</h5>
              <ul className="ms-3">
                <li>To match your face with photos from events you attended</li>
                <li>To deliver your personal gallery automatically</li>
                <li>To provide the core face recognition functionality</li>
              </ul>

              <h5 className="mt-4 mb-3">3.2 Biometric Data Retention</h5>
              <p>
                We retain your face matrix for as long as your account is active and for up to 12 months after account deletion, unless legally required to retain longer. You may request deletion of your biometric data at any time.
              </p>

              <h5 className="mt-4 mb-3">3.3 GDPR Compliance (EU Users)</h5>
              <p>
                If you are a resident of the European Union, we process biometric data under GDPR Article 9 based on:
              </p>
              <ul className="ms-3">
                <li>Your explicit consent (obtained at registration)</li>
                <li>The necessity of processing for the Service to function</li>
                <li>Your legitimate interests in receiving your event photos</li>
              </ul>
              <p>
                You have the right to withdraw consent, request access to your data, or request deletion at any time.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">4. How We Use Your Information</h2>
              <p>
                We use collected information for:
              </p>
              <ul className="ms-3">
                <li>Providing and improving the Service</li>
                <li>Processing transactions and sending related communications</li>
                <li>Sending transactional and promotional emails</li>
                <li>Responding to your inquiries and customer support</li>
                <li>Analyzing usage patterns and optimizing features</li>
                <li>Detecting and preventing fraud or security issues</li>
                <li>Complying with legal obligations</li>
                <li>Personalizing your experience</li>
              </ul>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">5. How We Share Your Information</h2>

              <h5 className="mt-4 mb-3">5.1 Information We Do Not Share</h5>
              <p>
                We do <strong>not</strong> sell or rent your personal data to third parties for marketing purposes.
              </p>

              <h5 className="mt-4 mb-3">5.2 Information We May Share</h5>
              <ul className="ms-3">
                <li><strong>Service Providers:</strong> Payment processors, cloud hosting providers, analytics services (all under confidentiality agreements)</li>
                <li><strong>Event Organizers:</strong> Limited information about your participation (only with your consent or as part of event management)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>Business Transfers:</strong> In case of merger, acquisition, or bankruptcy (you will be notified)</li>
              </ul>

              <h5 className="mt-4 mb-3">5.3 Photo Sharing</h5>
              <p>
                Your photos are delivered to the participants whose faces are detected in them. You can control visibility through your gallery privacy settings.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">6. Data Security</h2>
              <p>
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="ms-3">
                <li>SSL/TLS encryption for data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Regular security audits and penetration testing</li>
                <li>Limited employee access to personal data</li>
                <li>Secure deletion of biometric data upon request</li>
              </ul>
              <p>
                However, no security system is completely impenetrable. We cannot guarantee absolute security, and you use the Service at your own risk.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">7. Your Rights &amp; Choices</h2>

              <h5 className="mt-4 mb-3">7.1 Access &amp; Portability</h5>
              <p>
                You have the right to request a copy of the personal data we hold about you in a portable format.
              </p>

              <h5 className="mt-4 mb-3">7.2 Correction</h5>
              <p>
                You can update your account information anytime through your profile settings.
              </p>

              <h5 className="mt-4 mb-3">7.3 Deletion</h5>
              <p>
                You may request deletion of your account and associated data. We will delete your account within 30 days, except where we are legally required to retain information.
              </p>

              <h5 className="mt-4 mb-3">7.4 Opt-Out of Marketing</h5>
              <p>
                You can opt out of promotional emails by clicking "unsubscribe" in any email or by contacting us.
              </p>

              <h5 className="mt-4 mb-3">7.5 Cookie Management</h5>
              <p>
                You can control cookies through your browser settings. Disabling cookies may affect your ability to use certain features.
              </p>

              <h5 className="mt-4 mb-3">7.6 Do Not Track (DNT)</h5>
              <p>
                Our Service does not respond to DNT signals. We continue to collect data as described in this policy.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">8. Cookies &amp; Tracking Technologies</h2>
              <p>
                We use cookies and similar tracking technologies for:
              </p>
              <ul className="ms-3">
                <li>Remembering login information</li>
                <li>Tracking user preferences and settings</li>
                <li>Analyzing usage patterns (Google Analytics)</li>
                <li>Preventing fraud and security issues</li>
              </ul>
              <p>
                Types of cookies used:
              </p>
              <ul className="ms-3">
                <li><strong>Session Cookies:</strong> Expire when you close your browser</li>
                <li><strong>Persistent Cookies:</strong> Remain until expiration or deletion</li>
                <li><strong>Third-Party Cookies:</strong> From analytics and service providers</li>
              </ul>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">9. Data Retention</h2>
              <p>
                We retain your information as follows:
              </p>
              <ul className="ms-3">
                <li><strong>Account Data:</strong> While your account is active, then deleted after 30 days</li>
                <li><strong>Biometric Data:</strong> Until account deletion, then 12 months more for legal compliance</li>
                <li><strong>Transaction Records:</strong> 7 years (legal/tax requirement)</li>
                <li><strong>Analytics Data:</strong> 12-24 months (aggregated)</li>
                <li><strong>Support Communications:</strong> 2 years for legal protection</li>
              </ul>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">10. Third-Party Links &amp; Services</h2>
              <p>
                Our Service may contain links to third-party websites and integrations (Instagram, WhatsApp, TikTok, Facebook). We are not responsible for their privacy practices. Please review their privacy policies before sharing your information.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">11. Children's Privacy</h2>
              <p>
                The Service is not intended for individuals under 13 years old. We do not knowingly collect data from children under 13. If we become aware of such collection, we will take steps to delete the information and terminate the child's account. Parents who believe their child has provided information should contact us immediately.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">12. GDPR Rights (EU Users)</h2>
              <p>
                If you are a resident of the European Union, you have the following rights under GDPR:
              </p>
              <ul className="ms-3">
                <li><strong>Right of Access:</strong> Request a copy of your data</li>
                <li><strong>Right of Rectification:</strong> Correct inaccurate data</li>
                <li><strong>Right of Erasure:</strong> Delete your data ("Right to be Forgotten")</li>
                <li><strong>Right of Restriction:</strong> Restrict processing</li>
                <li><strong>Right of Portability:</strong> Receive data in portable format</li>
                <li><strong>Right to Object:</strong> Object to certain processing</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for biometric processing</li>
              </ul>
              <p>
                To exercise these rights, contact us at support@pic-loud.com. We will respond within 30 days.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">13. International Data Transfers</h2>
              <p>
                Your information may be transferred to, stored in, and processed in countries other than your country of residence. These countries may have data protection laws that differ from your home country. By using the Service, you consent to such transfers.
              </p>
              <p>
                For EU users, we ensure appropriate safeguards through Standard Contractual Clauses or adequacy decisions.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">14. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy at any time. We will notify you of significant changes via email or through the Service. Continued use of the Service constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">15. Contact Us</h2>
              <p>
                For questions about this Privacy Policy or to exercise your rights, please contact:
              </p>
              <p>
                <strong>Johann Thuillier</strong><br />
                Pic-Loud<br />
                25 Yehuda Halevi<br />
                4248025 Netanya, Israel<br />
                Email: support@pic-loud.com<br />
                Phone: +972-58-5052871
              </p>
              <p>
                <strong>Data Protection Officer:</strong><br />
                If you have concerns about our data practices, you may contact our DPO or file a complaint with your local data protection authority.
              </p>
            </section>

            <section className="mb-5">
              <h2 className="h4 mb-3">16. California Privacy Rights (CCPA)</h2>
              <p>
                If you are a California resident, you have the right to:
              </p>
              <ul className="ms-3">
                <li>Know what data is collected, used, and shared</li>
                <li>Delete personal data (with exceptions)</li>
                <li>Opt-out of the sale of personal data</li>
                <li>Non-discrimination for exercising these rights</li>
              </ul>
              <p>
                We do not sell personal data. To make a request, contact us at support@pic-loud.com.
              </p>
            </section>
          </div>
        </Container>
      </main>

      <footer className="mt-auto bg-light">
        <Container className="">
          <Tail />
        </Container>
      </footer>
    </div>
  );
}
